import { toPlainLowercase } from '../../../shared/helpers/nombre-texto.helper';

const ID_LIDER_EVADIR = 'evadir';
const ID_LIDER_NINGUNO = 'sin lider';

export interface PersonaOrganigrama {
  id: string;
  nombre: string;
  nombre2: string;
  apellido: string;
  lider: boolean;
  idLider: string;
  ministerio: string;
  sexo: string;
}

export interface NodoOrganigrama {
  id: string;
  nombre: string;
  nombre2: string;
  apellido: string;
  lider: boolean;
  esPastor: boolean;
  ministerio: string;
  sexo: string;
  hijos: NodoOrganigrama[];
}

export interface OrganigramaArmado {
  validos: NodoOrganigrama[];
  sueltos: NodoOrganigrama[];
  fuera: number;
}

function esValorEspecial(idLider: string): boolean {
  const valor = idLider.trim().toLowerCase();
  return valor === ID_LIDER_EVADIR || valor === ID_LIDER_NINGUNO;
}

function esEvadir(idLider: string): boolean {
  return idLider.trim().toLowerCase() === ID_LIDER_EVADIR;
}

function sinLider(idLider: string): boolean {
  const valor = idLider.trim().toLowerCase();
  return valor.length === 0 || valor === ID_LIDER_NINGUNO;
}

function tieneMinisterio(ministerio: string): boolean {
  const texto = ministerio.trim().toLowerCase();
  return texto.length > 0 && texto !== 'ninguno';
}

function comparar(a: PersonaOrganigrama, b: PersonaOrganigrama): number {
  const apellido = a.apellido.localeCompare(b.apellido, 'es', { sensitivity: 'base' });
  if (apellido !== 0) {
    return apellido;
  }
  return a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' });
}

function clavesNombre(persona: PersonaOrganigrama): string[] {
  const completo = toPlainLowercase(
    [persona.nombre, persona.nombre2, persona.apellido].filter((parte) => parte.trim()).join(' '),
  );
  const corto = toPlainLowercase(`${persona.nombre} ${persona.apellido}`);
  return [completo, corto];
}

function esPastorNombrado(persona: PersonaOrganigrama, nombrePastor: string): boolean {
  const buscado = toPlainLowercase(nombrePastor);
  return buscado.length > 0 && clavesNombre(persona).includes(buscado);
}

function indexarHijos(personas: PersonaOrganigrama[]): Map<string, PersonaOrganigrama[]> {
  const hijos = new Map<string, PersonaOrganigrama[]>();
  for (const persona of personas) {
    const idLider = persona.idLider.trim();
    if (!persona.id || !idLider || esValorEspecial(idLider) || idLider === persona.id) {
      continue;
    }
    const lista = hijos.get(idLider) ?? [];
    lista.push(persona);
    hijos.set(idLider, lista);
  }
  for (const lista of hijos.values()) {
    lista.sort(comparar);
  }
  return hijos;
}

function tienePadreSuelto(
  persona: PersonaOrganigrama,
  porId: Map<string, PersonaOrganigrama>,
  pendientes: Set<string>,
): boolean {
  const idLider = persona.idLider.trim();
  if (!idLider || idLider === persona.id || esValorEspecial(idLider)) {
    return false;
  }
  const padre = porId.get(idLider);
  return !!padre && padre.lider && pendientes.has(padre.id);
}

function construir(
  persona: PersonaOrganigrama,
  hijosPorLider: Map<string, PersonaOrganigrama[]>,
  visitados: Set<string>,
  esCima: boolean,
  esPastor: boolean,
): NodoOrganigrama {
  visitados.add(persona.id);
  const puedeTenerHijos = esCima || persona.lider;
  const hijos = puedeTenerHijos
    ? (hijosPorLider.get(persona.id) ?? [])
        .filter((hijo) => !visitados.has(hijo.id))
        .map((hijo) => construir(hijo, hijosPorLider, visitados, false, false))
    : [];

  return {
    id: persona.id,
    nombre: persona.nombre,
    nombre2: persona.nombre2,
    apellido: persona.apellido,
    lider: persona.lider,
    esPastor,
    ministerio: persona.ministerio,
    sexo: persona.sexo,
    hijos,
  };
}

/**
 * Cada persona con líder Evadir es la cima de un árbol válido.
 * Un líder que no cuelga de ninguna de esas cimas abre una pirámide aparte.
 * Quien tiene ministerio y no tiene líder también aparece como sin conexión.
 * El resto solo se cuenta.
 */
export function armarOrganigrama(
  personas: PersonaOrganigrama[],
  nombrePastor: string,
): OrganigramaArmado {
  const validas = personas.filter((persona) => persona.id.trim().length > 0);
  const porId = new Map<string, PersonaOrganigrama>();
  for (const persona of validas) {
    if (!porId.has(persona.id)) {
      porId.set(persona.id, persona);
    }
  }

  const cimas = validas.filter((persona) => esEvadir(persona.idLider)).sort(comparar);
  const pastor = cimas.find((persona) => esPastorNombrado(persona, nombrePastor));
  const ordenCimas = pastor ? [pastor, ...cimas.filter((persona) => persona.id !== pastor.id)] : cimas;
  const hijosPorLider = indexarHijos(validas);
  const visitados = new Set<string>();
  const validos = ordenCimas.map((cima) =>
    construir(cima, hijosPorLider, visitados, true, cima.id === pastor?.id),
  );

  const lideresSueltos = validas
    .filter((persona) => persona.lider && !visitados.has(persona.id))
    .sort(comparar);
  const pendientes = new Set(lideresSueltos.map((persona) => persona.id));
  const sueltos: NodoOrganigrama[] = [];

  while (pendientes.size > 0) {
    const raiz =
      lideresSueltos.find(
        (persona) => pendientes.has(persona.id) && !tienePadreSuelto(persona, porId, pendientes),
      ) ?? lideresSueltos.find((persona) => pendientes.has(persona.id));
    if (!raiz) {
      break;
    }
    sueltos.push(construir(raiz, hijosPorLider, visitados, false, false));
    for (const id of [...pendientes]) {
      if (visitados.has(id)) {
        pendientes.delete(id);
      }
    }
  }

  const conMinisterioSinLider = validas
    .filter(
      (persona) =>
        !visitados.has(persona.id) && sinLider(persona.idLider) && tieneMinisterio(persona.ministerio),
    )
    .sort(comparar);
  for (const persona of conMinisterioSinLider) {
    sueltos.push(construir(persona, hijosPorLider, visitados, false, false));
  }

  const fuera = validas.filter((persona) => !visitados.has(persona.id)).length;
  return { validos, sueltos, fuera };
}
